import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Bell, Save, Send, Loader2, CheckCircle2, XCircle, Slack, MessageCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';

/**
 * Admin → System Health → Alerting configuration panel.
 *
 * Persisted in system_configs.alerts_config. Fires on every 500 captured
 * by ServerErrorTrackerMiddleware (per-error), plus when the sliding
 * 5-min error count crosses the configured threshold (dedup'd per
 * window).
 */
export const AlertingConfigPanel = () => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({
    slack_webhook_url: '',
    discord_webhook_url: '',
    alert_email: '',
    threshold_count: 10,
    threshold_window_minutes: 5,
    enabled: true,
  });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/alerts');
      setCfg(r.data);
      setForm((prev) => ({
        ...prev,
        slack_webhook_url: '',  // Never prefill masked value
        discord_webhook_url: '',
        alert_email: r.data.alert_email || '',
        threshold_count: r.data.threshold_count || 10,
        threshold_window_minutes: r.data.threshold_window_minutes || 5,
        enabled: r.data.enabled !== false,
      }));
    } catch (e) {
      toast.error('Failed to load alerting config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only include webhook URLs if the admin actually typed a new one;
      // an empty string means "clear the stored value", so we send it
      // explicitly. If the field is empty AND the existing cfg has a
      // value, we preserve the existing.
      const payload = { ...form };
      if (!form.slack_webhook_url && cfg?.has_slack) {
        delete payload.slack_webhook_url;
      }
      if (!form.discord_webhook_url && cfg?.has_discord) {
        delete payload.discord_webhook_url;
      }
      await api.post('/admin/alerts', payload);
      toast.success('Alerting configuration saved');
      await load();
      // Reset input fields
      setForm((prev) => ({
        ...prev,
        slack_webhook_url: '',
        discord_webhook_url: '',
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleClearWebhook = async (which) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        [which]: '',
      };
      // Ensure the other URL is preserved
      if (which === 'slack_webhook_url' && cfg?.has_discord && !form.discord_webhook_url) {
        delete payload.discord_webhook_url;
      }
      if (which === 'discord_webhook_url' && cfg?.has_slack && !form.slack_webhook_url) {
        delete payload.slack_webhook_url;
      }
      await api.post('/admin/alerts', payload);
      toast.success(`${which === 'slack_webhook_url' ? 'Slack' : 'Discord'} webhook cleared`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Clear failed');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    try {
      const r = await api.post('/admin/alerts/test');
      if (r.data?.ok) {
        const channels = [];
        if (r.data.slack_configured) channels.push('Slack');
        if (r.data.discord_configured) channels.push('Discord');
        if (r.data.email_configured) channels.push('Email');
        if (channels.length === 0) {
          toast.warning('No channels configured yet — save a webhook or email first.');
        } else {
          toast.success(`Test alert sent to: ${channels.join(', ')}`);
        }
      } else {
        toast.error(r.data?.error || 'Test alert failed');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Card className="bg-white border-slate-200 shadow-sm" data-testid="admin-alerts-panel">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div className="min-w-0">
          <CardTitle className="text-slate-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" />
            Alerting
            {cfg?.enabled === false && (
              <Badge className="bg-slate-100 text-slate-700 border-slate-200">Disabled</Badge>
            )}
          </CardTitle>
          <CardDescription className="text-slate-500 mt-1">
            Get notified in Slack, Discord, or by email the moment a server error is captured — or when
            error volume spikes above your threshold.
          </CardDescription>
        </div>
        <Button
          variant="outline" size="sm"
          onClick={handleTest} disabled={testing || loading}
          data-testid="alerts-test-btn"
        >
          {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          <span className="ml-1">Send test alert</span>
        </Button>
      </CardHeader>
      <CardContent className="space-y-6">
        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-500">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            {/* Master enable switch */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
              <div>
                <div className="text-sm font-medium text-slate-900">Alerting enabled</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  When off, no webhooks or emails are sent even if channels are configured.
                </div>
              </div>
              <Switch
                checked={form.enabled}
                onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                data-testid="alerts-enabled-switch"
              />
            </div>

            {/* Slack webhook */}
            <div className="space-y-2" data-testid="alerts-slack-section">
              <Label className="text-sm font-medium text-slate-900 flex items-center gap-2">
                <Slack className="w-4 h-4 text-[#4A154B]" />
                Slack incoming webhook URL
                {cfg?.has_slack ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Configured
                  </Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 border-slate-200 gap-1">
                    <XCircle className="w-3 h-3" /> Not set
                  </Badge>
                )}
              </Label>
              {cfg?.has_slack && (
                <div className="text-xs text-slate-500 font-mono">
                  Current: {cfg.slack_webhook_url_masked}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="https://hooks.slack.com/services/..."
                  value={form.slack_webhook_url}
                  onChange={(e) => setForm({ ...form, slack_webhook_url: e.target.value })}
                  data-testid="alerts-slack-input"
                />
                {cfg?.has_slack && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleClearWebhook('slack_webhook_url')}
                    disabled={saving}
                    data-testid="alerts-slack-clear"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-500">
                Create at <a href="https://api.slack.com/messaging/webhooks" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">api.slack.com/messaging/webhooks</a>. Must be https://.
              </p>
            </div>

            {/* Discord webhook */}
            <div className="space-y-2" data-testid="alerts-discord-section">
              <Label className="text-sm font-medium text-slate-900 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-[#5865F2]" />
                Discord webhook URL
                {cfg?.has_discord ? (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Configured
                  </Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 border-slate-200 gap-1">
                    <XCircle className="w-3 h-3" /> Not set
                  </Badge>
                )}
              </Label>
              {cfg?.has_discord && (
                <div className="text-xs text-slate-500 font-mono">
                  Current: {cfg.discord_webhook_url_masked}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  type="url"
                  placeholder="https://discord.com/api/webhooks/..."
                  value={form.discord_webhook_url}
                  onChange={(e) => setForm({ ...form, discord_webhook_url: e.target.value })}
                  data-testid="alerts-discord-input"
                />
                {cfg?.has_discord && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => handleClearWebhook('discord_webhook_url')}
                    disabled={saving}
                    data-testid="alerts-discord-clear"
                  >
                    Clear
                  </Button>
                )}
              </div>
              <p className="text-[11px] text-slate-500">
                Create in Server Settings → Integrations → Webhooks. Must be https://.
              </p>
            </div>

            {/* Email threshold */}
            <div className="space-y-2 pt-2 border-t border-slate-100" data-testid="alerts-email-section">
              <Label className="text-sm font-medium text-slate-900 flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-600" />
                Threshold email recipient
              </Label>
              <Input
                type="email"
                placeholder="alerts@yourcompany.com"
                value={form.alert_email}
                onChange={(e) => setForm({ ...form, alert_email: e.target.value })}
                data-testid="alerts-email-input"
              />
              <p className="text-[11px] text-slate-500">
                Sent via admin-configured SMTP when the error surge threshold is crossed. Leave blank to skip email.
              </p>
            </div>

            {/* Threshold config */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-900">
                  Threshold count
                </Label>
                <Input
                  type="number" min={1} max={1000}
                  value={form.threshold_count}
                  onChange={(e) => setForm({ ...form, threshold_count: parseInt(e.target.value, 10) || 10 })}
                  data-testid="alerts-threshold-count"
                />
                <p className="text-[11px] text-slate-500">
                  Errors within the window that trigger a threshold alert.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-900">
                  Window (minutes)
                </Label>
                <Input
                  type="number" min={1} max={1440}
                  value={form.threshold_window_minutes}
                  onChange={(e) => setForm({ ...form, threshold_window_minutes: parseInt(e.target.value, 10) || 5 })}
                  data-testid="alerts-threshold-window"
                />
                <p className="text-[11px] text-slate-500">
                  Sliding window size. Alert fires at most once per window.
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={handleSave} disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="alerts-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                <span className="ml-2">Save alerting config</span>
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default AlertingConfigPanel;
